import { EZoneCategory, SiteEntryProps, Zone, ZoneEntry } from '@ixicare/resources';
import * as React from 'react';
import DrawToolMap from '../components/DrawToolMap/DrawToolMap'; // Corrected import path
import DrawToolSidebar from '../components/DrawToolSidebar';
import DrawToolCreateContainer from './DrawToolCreateContainer';
import DrawToolUpdateContainer from './DrawToolUpdateContainer';
import { MapPostEventWrapperRef } from '~/components/Map/MapPostEventWrapper'; // Import the ref interface
import { MapEvent } from '~/config/map'; // Import MapEvent

interface DrawToolMapContainerProps {
  site: SiteEntryProps;
  planeId: number;
}

const DrawToolMapContainer: React.FC<DrawToolMapContainerProps> = ({ site, planeId }) => {
  const mapRef = React.useRef<MapPostEventWrapperRef>(null); // Create the ref
  const [selectedZoneUuid, setSelectedZoneUuid] = React.useState<string>();
  const [isCreate, setIsCreate] = React.useState(false);
  const [zones, setZones] = React.useState<ZoneEntry[]>([]);
  const [currentGeoObject, setCurrentGeoObject] = React.useState<any>(null);

  React.useEffect(() => {
    Zone.getZones({ plane: planeId }).then((data) => {
      setZones(data);
    });
  }, [planeId]);

  const plane = React.useMemo(() => {
    return site.planes.find((p) => p.id === planeId);
  }, [site, planeId]);

  const selectedZone = React.useMemo(() => {
    const selectedZone = selectedZoneUuid ? zones.find((z) => z.uuid === selectedZoneUuid) : undefined;
    return selectedZone;
  }, [selectedZoneUuid, zones]);

  const handleZoneSelect = React.useCallback((zoneUuid: string) => {
    setSelectedZoneUuid(zoneUuid);
    setCurrentGeoObject(null);
    if (zoneUuid) setIsCreate(false);
  }, []);

  const handleSetIsCreate = React.useCallback((shouldCreate: boolean) => {
    setIsCreate(shouldCreate);
    setCurrentGeoObject(null);
    setSelectedZoneUuid(undefined);
    mapRef.current?.sendEvent(MapEvent.ZoneDrawCreateDrawingClick, shouldCreate);
  }, []);

  const handleDeleteSelectedZone = React.useCallback(() => {
    if (selectedZone) {
      Zone.deleteZone(selectedZone.uuid).then(() => {
        setZones((zones) => [...zones.filter((z) => z.uuid !== selectedZone.uuid)]);
      });
    }
  }, [selectedZone]);

  const handleUpdateSelectedZone = React.useCallback(
    (props: { name: string; livingUnitId?: string; category?: EZoneCategory }) => {
      if (selectedZone) {
        Zone.patchZone(selectedZone.uuid, {
          ...props,
          geo: currentGeoObject
            ? {
                geometry: currentGeoObject.geometry,
                type: currentGeoObject.type,
              }
            : undefined,
        }).then((response) => {
          setZones((zones) => [...zones.map((z) => (z.uuid === selectedZone.uuid ? response : z))]);
          setSelectedZoneUuid(undefined);
        });
      }
    },
    [selectedZone, currentGeoObject]
  );

  const handleCreateZone = React.useCallback(
    (params: { name: string; livingUnitId?: string; category?: EZoneCategory }) => {
      if (currentGeoObject && plane) {
        Zone.createZone({
          ...params,
          planeUuid: plane.uuid,
          geo: currentGeoObject,
        }).then((response) => {
          setZones((zones) => [...zones, response]);
          setSelectedZoneUuid(undefined);
          setIsCreate(false);
        });
      }
    },
    [currentGeoObject, plane]
  );

  return (
    <>
      {plane && (
        <DrawToolMap
          siteId={site.id}
          planeUuid={plane.uuid}
          isCreate={isCreate}
          zones={zones}
          onZoneSelect={handleZoneSelect}
          onZoneDrawCreateOrUpdate={setCurrentGeoObject}
          ref={mapRef} // Pass the ref here
        />
      )}
      <DrawToolSidebar
        handleToggleCreate={handleSetIsCreate}
        floorNumber={plane ? plane.name : '-'}
        isCreate={isCreate}
        name={site.name}
      >
        {isCreate && (
          <DrawToolCreateContainer site={site} onCreate={handleCreateZone} hasDrawing={!!currentGeoObject} />
        )}
        {selectedZone && (
          <DrawToolUpdateContainer
            site={site}
            zone={selectedZone}
            hasUpdatedGeo={!!currentGeoObject}
            onUpdate={handleUpdateSelectedZone}
            onDelete={handleDeleteSelectedZone}
          />
        )}
      </DrawToolSidebar>
    </>
  );
};

export default DrawToolMapContainer;
